import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UsersFourIcon as UsersFour, ArrowRightIcon as ArrowRight, GlobeIcon as Globe, ShieldCheckIcon as ShieldCheck, CrownIcon as Crown, StorefrontIcon as Storefront, HeartIcon as Heart } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Dashboard onboarding nudge for users who haven't created a kennel
 * profile yet. Rendered ONLY when `/api/breeders/mine` returns no
 * kennel — once one exists, this card unmounts and the existing
 * VerifiedSealKitAnnouncement / dashboard widgets take over.
 *
 * The card sells the "why" (public SEO page, verified seal, Elite
 * Verified eligibility, etc.) — not just the "what" — because the
 * single biggest reason breeders fail to convert into paid tiers is
 * that nobody told them why a kennel profile mattered.
 *
 * CTA navigates to `/breeders?create=1` so the existing Add Breeder
 * dialog auto-opens (handled in Breeders.jsx).
 */
export default function CreateKennelNudge({ persona }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, hasKennel: false });

  useEffect(() => {
    let cancelled = false;
    api.get("/breeders/mine")
      .then((r) => {
        const has = !!r.data?.id;
        if (!cancelled) setState({ loading: false, hasKennel: has });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, hasKennel: false });
      });
    return () => { cancelled = true; };
  }, []);

  // Kennel profiles are a BREEDER concept. Dog owners, buyers,
  // transporters, providers, and storefront sellers should never be
  // nudged to "set up a kennel" — it reads as wrong-audience noise.
  // Legacy accounts with no declared/resolved persona are also skipped:
  // real breeders get resolved to "breeder" by the nav-context resolver.
  if (persona !== "breeder") return null;
  if (state.loading || state.hasKennel) return null;

  const benefits = [
    {
      icon: Globe,
      title: "Public SEO profile",
      copy: "A polished `/k/your-name` page Google can index — your kennel's calling card.",
    },
    {
      icon: ShieldCheck,
      title: "Verified seal eligibility",
      copy: "Pass 7 verification checks to unlock the WaxSeal medallion buyers trust.",
    },
    {
      icon: Crown,
      title: isNativeApp() ? "Top-of-directory placement" : "Elite Verified tier",
      copy: "Rise to the top of the Breeders directory, Marketplace, Breeding Matchmaker & Cmd+K search.",
    },
    {
      icon: Storefront,
      title: "Unlock the Marketplace",
      copy: "List dogs, plan litters, take deposits — all flow through your kennel.",
    },
    {
      icon: Heart,
      title: "Followers & watchlist",
      copy: "Buyers can watch your kennel and get notified the moment a new litter is announced.",
    },
  ];

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#C9A227]/40 bg-gradient-to-br from-[#FBF7EC] via-white to-[#F4E5B0]/50 p-6 md:p-8 mb-6 shadow-luxury-light"
      data-testid="create-kennel-nudge"
    >
      <div
        aria-hidden
        className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#C9A227]/15 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-[#C1272D]/8 blur-3xl pointer-events-none"
      />

      <div className="relative grid lg:grid-cols-5 gap-6">
        {/* Header column */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#FBF7EC] to-[#F3E9C7] ring-1 ring-[#C9A227]/40 shadow-[0_2px_6px_rgba(193,162,39,0.15)] mb-4">
            <UsersFour size={26} weight="duotone" className="text-[#8A6A00]" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Get set up</p>
          <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-[1.05]">
            Create your kennel page
          </h2>
          <p className="text-sm text-[#3a3a3a] mt-3 leading-relaxed max-w-md">
            Your kennel profile is the front door to everything you do on Armstrong — buyers, badges, breedings, payouts. Set it up in under a minute.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => navigate("/breeders?create=1")}
              className="rounded-full bg-ink hover:bg-crimson text-white px-5 h-11 transition-colors"
              data-testid="create-kennel-nudge-cta"
            >
              Set up my kennel
              <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Button>
            <span className="text-xs text-[#6B6B6B]">Takes about 60 seconds · free forever</span>
          </div>
        </div>

        {/* Benefits column */}
        <div className="lg:col-span-3">
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.title} className="flex gap-3" data-testid={`kennel-benefit-${b.title.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/70 backdrop-blur-sm ring-1 ring-[#E2DAC2] flex items-center justify-center mt-0.5">
                    <Icon size={16} weight="duotone" className="text-[#8A6A00]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink leading-snug">{b.title}</p>
                    <p className="text-[12.5px] text-[#6B6B6B] leading-relaxed mt-0.5">{b.copy}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
